import React, { useState, useEffect, useRef } from "react";
import { Search, Calendar, PlayCircle, Clock } from "lucide-react";
import { API_ENDPOINTS } from "../../../constants/constants";
// Import pour le SDK Zoom
import "../../../styles/zoom-override.css";
import { useNavigate } from "react-router-dom";

// Version du SDK Zoom
const ZOOM_SDK_VERSION = "2.18.2";

// URLs pour le SDK Zoom - avec solution de proxy sécurisée
const ZOOM_SDK_URLS = {
  // URLs originales (susceptibles d'être bloquées par CORS)
  original: {
    react: `https://source.zoom.us/${ZOOM_SDK_VERSION}/lib/vendor/react.min.js`,
    sdk: `https://source.zoom.us/${ZOOM_SDK_VERSION}/lib/zoomus-websdk-embedded.umd.min.js`,
  },
  // URLs de proxy sécurisées via notre backend
  proxy: {
    react: API_ENDPOINTS.MEETINGS.ZOOM_PROXY.SDK.REACT(ZOOM_SDK_VERSION),
    sdk: API_ENDPOINTS.MEETINGS.ZOOM_PROXY.SDK.SDK(ZOOM_SDK_VERSION),
    css: API_ENDPOINTS.MEETINGS.ZOOM_PROXY.SDK.CSS,
  },
  // CDNs publics pour React (en dernier recours)
  cdn: {
    react: "https://unpkg.com/react@17/umd/react.production.min.js",
    // Note: pour le SDK Zoom, on devra toujours utiliser l'original ou le proxy
  },
};

// Vérification immédiate si window.ZoomEmbedded existe déjà dans le contexte global
try {
  console.log("🔍 Vérification initiale de Zoom:", {
    windowExists: typeof window !== "undefined",
    ZoomEmbeddedExists:
      typeof window !== "undefined" && "ZoomEmbedded" in window,
  });
} catch (e) {
  console.error("❌ Erreur lors de la vérification initiale de Zoom:", e);
}

// Logs au démarrage pour vérifier l'environnement
console.log("📌 LiveDirect - Constantes et environnement:", {
  ZOOM_SDK_VERSION,
  apiEndpoint: API_ENDPOINTS.MEETINGS.GET_ZOOM_SIGNATURE,
  hasZoomKey: typeof import.meta.env.VITE_ZOOM_SDK_KEY !== "undefined",
});

// Déclarez une interface pour le client Zoom
interface ZoomClient {
  init: (config: {
    debug?: boolean;
    zoomAppRoot: HTMLElement;
    language?: string;
    customize?: {
      meetingInfo?: string[];
      toolbar?: {
        buttons?: {
          text: string;
          className: string;
          onClick: () => void;
        }[];
      };
    };
  }) => void;
  join: (options: {
    meetingNumber: string;
    signature: string;
    sdkKey: string;
    userName: string;
    userEmail?: string;
    password?: string;
    success?: (success: unknown) => void;
    error?: (error: unknown) => void;
  }) => void;
}

// Accès global à ZoomEmbeddedClient et au proxy Zoom
declare global {
  interface Window {
    ZoomEmbedded: {
      createClient: () => ZoomClient;
    };
    zoomProxy?: {
      init: (config: {
        debug?: boolean;
        apiUrl: string;
        sdkKey: string;
        userInfo: {
          name: string;
          email: string;
        };
      }) => void;
    };
  }
}

interface Meeting {
  id: string;
  topic: string;
  description: string;
  meetingId: string;
  joinUrl: string;
  zoomMeetingId?: string;
  zoomJoinUrl?: string;
  password: string;
  startTime: string;
  duration: number;
  timezone: string;
  isRecurring: boolean;
  creatorId: string;
  createdBy?: number; // ID du créateur dans le format numérique
  creator?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    profileImage?: string;
    role?: string;
  };
  invitedUserIds: string[];
  createdAt: string;
  updatedAt: string;
}

// Determine meeting status based on start time and duration
const getMeetingStatus = (
  startTime: string,
  duration: number
): "upcoming" | "live" | "past" => {
  const meetingStart = new Date(startTime);
  const meetingEnd = new Date(meetingStart.getTime() + duration * 60 * 1000);
  const now = new Date();

  if (now < meetingStart) {
    return "upcoming";
  } else if (now >= meetingStart && now <= meetingEnd) {
    return "live";
  } else {
    return "past";
  }
};

// Format date for display
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// Format time for display
const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Format duration for display
const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours > 0) {
    return `${hours}h${mins > 0 ? mins : ""}`;
  }
  return `${mins}min`;
};

// Fonction pour obtenir le nom du créateur de la réunion à partir des données creator
const getCreatorName = (meeting: Meeting): string => {
  // Si nous avons déjà les informations du créateur directement dans l'objet meeting
  if (meeting.creator && meeting.creator.firstName) {
    return `${meeting.creator.firstName} ${
      meeting.creator.lastName || ""
    }`.trim();
  }

  // Fallback: Vérifier si c'est l'utilisateur courant
  if (meeting.creatorId) {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.id === meeting.creatorId || user.id === meeting.createdBy) {
          return user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : "Vous";
        }
      } catch (e) {
        console.error(
          "Erreur lors de la récupération des infos utilisateur:",
          e
        );
      }
    }

    // Fallback générique
    return `Organisateur de réunion`;
  }

  // Valeur par défaut
  return "Réunion Zoom";
};

const LiveMeetingCard: React.FC<{
  meeting: Meeting;
  onJoinMeeting: (meeting: Meeting) => void;
}> = ({ meeting, onJoinMeeting }) => {
  // Utiliser un avatar par défaut ou l'image de profil du créateur si disponible
  const defaultAvatar = "/images/avatar-placeholder.png";
  const creatorAvatar = meeting.creator?.profileImage
    ? `${import.meta.env.VITE_API_URL}/${meeting.creator.profileImage}`
    : defaultAvatar;
  const hostName = getCreatorName(meeting); // Utiliser la fonction pour obtenir le nom du créateur

  return (
    <div className="bg-gradient-to-b from-gray-700 to-gray-900 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow max-w-xs w-full mx-auto h-full flex flex-col pb-4">
      {/* Badge "En live" */}
      <div className="flex justify-between items-center px-4 py-3">
        <div className="flex items-center">
          <div className="bg-green-600 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center">
            <span>En live</span>
            <PlayCircle size={14} className="ml-1" />
          </div>
        </div>
        <div className="text-orange-500">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 3V21M18 6L18 18M6 8L6 16"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="px-4 py-0 text-white flex-grow flex flex-col h-full">
        {/* Titre en haut */}
        <div>
          <div className="uppercase font-light text-sm mb-2">THÈME:</div>
          <h2 className="text-lg font-bold mb-4 uppercase">{meeting.topic}</h2>
        </div>

        {/* Espace flexible qui pousse le reste vers le bas */}
        <div className="flex-1"></div>

        {/* Partie inférieure */}
        <div>
          {/* Informations de date et heure */}
          <div className="mb-4 bg-gray-800/60 rounded-md p-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center text-gray-300">
                <Calendar size={14} className="mr-2" />
                <span className="text-sm">{formatDate(meeting.startTime)}</span>
              </div>
              <div className="flex items-center text-gray-300">
                <Clock size={14} className="mr-2" />
                <span className="text-sm">{formatTime(meeting.startTime)}</span>
              </div>
              <div className="flex items-center text-gray-300 col-span-2">
                <span className="text-sm ml-6">
                  Durée: {formatDuration(meeting.duration)}
                </span>
              </div>
            </div>
          </div>

          {/* Description si disponible */}
          {meeting.description && (
            <div className="mb-4">
              <p className="text-sm text-gray-300">{meeting.description}</p>
            </div>
          )}

          {/* Photo de profil en cercle */}
          <div className="flex flex-col items-center mb-4">
            <div className="w-32 h-32 rounded-full overflow-hidden border-2 border-gray-500">
              <img
                src={creatorAvatar}
                alt={hostName}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-white mt-4 text-base font-semibold">
              {hostName}
            </div>
          </div>

          {/* Bouton pour rejoindre la réunion */}
          <button
            onClick={() => onJoinMeeting(meeting)}
            className="w-full h-12 py-2 bg-green-600 hover:bg-green-700 rounded-md text-white text-sm font-medium flex items-center justify-center"
          >
            <PlayCircle size={18} className="mr-2" />
            Rejoindre la réunion
          </button>
        </div>
      </div>
    </div>
  );
};

const UpcomingMeetingCard: React.FC<{
  meeting: Meeting;
  onJoinMeeting: (meeting: Meeting) => void;
}> = ({ meeting, onJoinMeeting }) => {
  // Utiliser un avatar par défaut ou l'image de profil du créateur si disponible
  const defaultAvatar = "/images/avatar-placeholder.png";
  const creatorAvatar = meeting.creator?.profileImage
    ? `${import.meta.env.VITE_API_URL}/${meeting.creator.profileImage}`
    : defaultAvatar;
  const hostName = getCreatorName(meeting); // Utiliser la fonction pour obtenir le nom du créateur

  return (
    <div className="bg-gradient-to-b from-gray-700 to-gray-900 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow max-w-xs w-full mx-auto h-full flex flex-col pb-4">
      {/* Badge "À venir" */}
      <div className="flex justify-between items-center px-4 py-3">
        <div className="flex items-center">
          <div className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center">
            <span>À venir</span>
            <Calendar size={14} className="ml-1" />
          </div>
        </div>
        <div className="text-orange-500">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.36 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.63 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z"
              fill="currentColor"
            />
          </svg>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="px-4 py-0 text-white flex-grow flex flex-col h-full">
        {/* Titre en haut */}
        <div>
          <div className="uppercase font-light text-sm mb-2">THÈME:</div>
          <h2 className="text-lg font-bold mb-4 uppercase">{meeting.topic}</h2>
        </div>

        {/* Espace flexible qui pousse le reste vers le bas */}
        <div className="flex-1"></div>

        {/* Partie inférieure */}
        <div>
          {/* Informations de date et heure */}
          <div className="mb-4 bg-gray-800/60 rounded-md p-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center text-gray-300">
                <Calendar size={14} className="mr-2" />
                <span className="text-sm">{formatDate(meeting.startTime)}</span>
              </div>
              <div className="flex items-center text-gray-300">
                <Clock size={14} className="mr-2" />
                <span className="text-sm">{formatTime(meeting.startTime)}</span>
              </div>
              <div className="flex items-center text-gray-300 col-span-2">
                <span className="text-sm ml-6">
                  Durée: {formatDuration(meeting.duration)}
                </span>
              </div>
            </div>
          </div>

          {/* Description si disponible */}
          {meeting.description && (
            <div className="mb-4">
              <p className="text-sm text-gray-300">{meeting.description}</p>
            </div>
          )}

          {/* Photo de profil en cercle */}
          <div className="flex flex-col items-center mb-4">
            <div className="w-32 h-32 rounded-full overflow-hidden border-2 border-gray-500">
              <img
                src={creatorAvatar}
                alt={hostName}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-white mt-4 text-base font-semibold">
              {hostName}
            </div>
          </div>

          {/* Détails de connexion */}
          <div className="mb-4 bg-gray-800/60 rounded-md p-3">
            <div className="text-sm text-gray-300">
              <div className="mb-2">ID de réunion: {meeting.meetingId}</div>
              {meeting.password && <div>Mot de passe: {meeting.password}</div>}
            </div>
          </div>

          {/* Bouton pour rejoindre la réunion */}
          <button
            onClick={() => onJoinMeeting(meeting)}
            className="w-full h-12 py-2 bg-orange-500 hover:bg-orange-600 rounded-md text-white text-sm font-medium flex items-center justify-center"
          >
            <Calendar size={18} className="mr-2" />
            Se connecter à la réunion
          </button>
        </div>
      </div>
    </div>
  );
};

const PastMeetingCard: React.FC<{
  meeting: Meeting;
  onJoinMeeting: (meeting: Meeting) => void;
}> = ({ meeting, onJoinMeeting }) => {
  // Utiliser un avatar par défaut ou l'image de profil du créateur si disponible
  const defaultAvatar = "/images/avatar-placeholder.png";
  const creatorAvatar = meeting.creator?.profileImage
    ? `${import.meta.env.VITE_API_URL}/${meeting.creator.profileImage}`
    : defaultAvatar;
  const hostName = getCreatorName(meeting); // Utiliser la fonction pour obtenir le nom du créateur

  return (
    <div className="bg-gradient-to-b from-gray-700 to-gray-900 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow max-w-xs w-full mx-auto h-full flex flex-col pb-4">
      {/* Badge "Terminé" */}
      <div className="flex justify-between items-center px-4 py-3">
        <div className="flex items-center">
          <div className="bg-gray-600 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center">
            <span>Terminé</span>
            <Clock size={14} className="ml-1" />
          </div>
        </div>
        <div className="text-gray-400">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21 8V21H3V8"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M23 4H1V8H23V4Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10 12H14"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="px-4 py-0 text-white flex-grow flex flex-col h-full">
        {/* Titre en haut */}
        <div>
          <div className="uppercase font-light text-sm mb-2">THÈME:</div>
          <h2 className="text-lg font-bold mb-4 uppercase">{meeting.topic}</h2>
        </div>

        {/* Espace flexible qui pousse le reste vers le bas */}
        <div className="flex-1"></div>

        {/* Partie inférieure */}
        <div>
          {/* Informations de date et heure */}
          <div className="mb-4 bg-gray-800/60 rounded-md p-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center text-gray-300">
                <Calendar size={14} className="mr-2" />
                <span className="text-sm">{formatDate(meeting.startTime)}</span>
              </div>
              <div className="flex items-center text-gray-300">
                <Clock size={14} className="mr-2" />
                <span className="text-sm">{formatTime(meeting.startTime)}</span>
              </div>
              <div className="flex items-center text-gray-300 col-span-2">
                <span className="text-sm ml-6">
                  Durée: {formatDuration(meeting.duration)}
                </span>
              </div>
            </div>
          </div>

          {/* Description si disponible */}
          {meeting.description && (
            <div className="mb-4">
              <p className="text-sm text-gray-300">{meeting.description}</p>
            </div>
          )}

          {/* Photo de profil en cercle */}
          <div className="flex flex-col items-center mb-4">
            <div className="w-32 h-32 rounded-full overflow-hidden border-2 border-gray-500">
              <img
                src={creatorAvatar}
                alt={hostName}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-white mt-4 text-base font-semibold">
              {hostName}
            </div>
          </div>

          {/* Bouton pour revoir la réunion enregistrée */}
          <button
            onClick={() => onJoinMeeting(meeting)}
            className="w-full h-12 py-2 bg-gray-600 hover:bg-gray-700 rounded-md text-white text-sm font-medium flex items-center justify-center"
          >
            <PlayCircle size={18} className="mr-2" />
            Revoir l'enregistrement
          </button>
        </div>
      </div>
    </div>
  );
};

// Fonction pour vérifier si nous sommes dans un environnement de navigateur
const isBrowser = typeof window !== "undefined";

// Fonction d'initialisation de Zoom SDK à exécuter immédiatement
(function preloadZoomSDK() {
  if (!isBrowser) return;

  console.log("Préchargement du SDK Zoom...");

  // Vérifier si ZoomEmbedded existe déjà
  if (window.ZoomEmbedded) {
    console.log("ZoomEmbedded est déjà chargé:", window.ZoomEmbedded);
    return;
  }

  try {
    // Charger le script React SDK via notre proxy
    const reactScript = document.createElement("script");
    reactScript.src = ZOOM_SDK_URLS.proxy.react;
    reactScript.async = false;
    reactScript.defer = false;
    reactScript.crossOrigin = "anonymous";

    // Charger le script Zoom SDK via notre proxy
    const zoomScript = document.createElement("script");
    zoomScript.src = ZOOM_SDK_URLS.proxy.sdk;
    zoomScript.async = false;
    zoomScript.defer = false;
    zoomScript.crossOrigin = "anonymous";

    // Ajouter les scripts au document
    document.head.appendChild(reactScript);
    reactScript.onload = () => {
      document.head.appendChild(zoomScript);
      console.log("Scripts React et Zoom chargés avec succès");
    };

    // Définir un délai pour vérifier si ZoomEmbedded est chargé
    setTimeout(() => {
      if (!window.ZoomEmbedded) {
        console.warn("ZoomEmbedded n'a pas été chargé après le délai imparti");
        // Créer un objet factice pour éviter les erreurs
        window.ZoomEmbedded = {
          createClient: () => ({
            init: () => {},
            join: () => {},
          }),
        } as { createClient: () => ZoomClient };
      }
    }, 5000);
  } catch (error) {
    console.error("Erreur lors du préchargement du SDK Zoom:", error);
  }
})();

// Composant pour utiliser le SDK Zoom
const ZoomMeeting: React.FC<{
  meetingNumber: string;
  passWord: string;
  userName: string;
  userEmail: string;
  topic: string;
}> = ({ meetingNumber, passWord, userName, userEmail, topic }) => {
  console.log("🔍 ZoomMeeting - Rendu initial", {
    meetingNumber,
    userName,
    userEmail,
  });
  const zoomContainer = useRef<HTMLDivElement>(null);
  const [loadingState, setLoadingState] = useState<
    "loading" | "error" | "ready"
  >("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  // Using null client since we're going with iframe solution instead
  const client = null;
  const [sdkFailed, setSdkFailed] = useState(false);

  // Vérifier que les props nécessaires sont présentes
  useEffect(() => {
    console.log("🔍 ZoomMeeting - Props vérification", {
      hasMeetingNumber: !!meetingNumber,
      hasUserName: !!userName,
      hasUserEmail: !!userEmail,
      envVars: {
        hasZoomSDKKey: !!import.meta.env.VITE_ZOOM_SDK_KEY,
      },
    });
  }, [meetingNumber, userName, userEmail]);

  // Vérifier le conteneur
  useEffect(() => {
    console.log("🔍 ZoomMeeting - Vérification du conteneur", {
      containerExists: !!zoomContainer.current,
      containerDimensions: zoomContainer.current
        ? {
            width: zoomContainer.current.offsetWidth,
            height: zoomContainer.current.offsetHeight,
          }
        : null,
    });
  }, []);

  // Add this useEffect inside the ZoomMeeting component
  useEffect(() => {
    // Log immédiatement si ZoomEmbedded existe
    console.log("ZoomEmbeddedExists:", !!window.ZoomEmbedded);

    // Si ZoomEmbedded n'existe pas, essayer de le charger
    if (!window.ZoomEmbedded) {
      const loadZoomSDK = () => {
        return new Promise<void>((resolve, reject) => {
          try {
            // Charger le script React SDK via notre proxy
            const reactScript = document.createElement("script");
            reactScript.src = ZOOM_SDK_URLS.proxy.react;
            reactScript.crossOrigin = "anonymous";

            // Charger le script Zoom SDK via notre proxy
            const zoomScript = document.createElement("script");
            zoomScript.src = ZOOM_SDK_URLS.proxy.sdk;
            zoomScript.crossOrigin = "anonymous";

            // Ajouter les scripts au document
            document.head.appendChild(reactScript);
            reactScript.onload = () => {
              document.head.appendChild(zoomScript);
              zoomScript.onload = () => {
                console.log("SDK Zoom chargé avec succès via useEffect");
                resolve();
              };
              zoomScript.onerror = () => {
                console.error("Échec du chargement du SDK Zoom");
                reject(new Error("Échec du chargement du SDK Zoom"));
              };
            };
            reactScript.onerror = () => {
              console.error("Échec du chargement du SDK React");
              reject(new Error("Échec du chargement du SDK React"));
            };
          } catch (error) {
            console.error("Erreur lors du chargement du SDK Zoom:", error);
            reject(error);
          }
        });
      };

      loadZoomSDK()
        .then(() => {
          console.log(
            "ZoomEmbeddedExists après chargement:",
            !!window.ZoomEmbedded
          );
          setErrorMessage("");
        })
        .catch((error) => {
          console.error("Erreur lors du chargement du SDK Zoom:", error);
          setErrorMessage(
            "Impossible de charger le SDK Zoom. Veuillez réessayer ou utiliser le mode iframe."
          );
        });
    }
  }, []);

  // Charger les scripts Zoom de manière optimisée via notre proxy
  useEffect(() => {
    const loadZoomScripts = async () => {
      try {
        console.log(
          "🔍 Début du chargement des scripts Zoom via proxy sécurisé"
        );

        // Étape 1: Charger le script de configuration du proxy
        try {
          // Charger d'abord le script de setup qui configure l'environnement
          const setupScript = document.createElement("script");
          setupScript.src = API_ENDPOINTS.MEETINGS.ZOOM_PROXY.SETUP_SCRIPT;
          setupScript.crossOrigin = "anonymous";
          document.head.appendChild(setupScript);

          // Attendre que le script de setup soit chargé
          await new Promise((resolve, reject) => {
            setupScript.onload = resolve;
            setupScript.onerror = reject;
            setTimeout(
              () =>
                reject(
                  new Error("Timeout lors du chargement du script de setup")
                ),
              5000
            );
          });

          console.log("✅ Script de setup chargé avec succès");

          // Étape 2: Initialiser le proxy Zoom
          // Vérifier que window.zoomProxy est disponible
          if (!window.zoomProxy) {
            throw new Error(
              "Le proxy Zoom n'est pas disponible après chargement du script de setup"
            );
          }

          // Initialiser le proxy avec les informations utilisateur
          window.zoomProxy.init({
            // Ajouter ici les options de configuration du proxy
            debug: true,
            apiUrl: API_ENDPOINTS.AUTH.LOGIN.split("/api/")[0], // Extrait l'URL de base de l'API
            sdkKey: import.meta.env.VITE_ZOOM_SDK_KEY || "",
            userInfo: {
              name: userName, // Utilisera le nom construit à partir de firstName + lastName
              email: userEmail,
            },
          });

          console.log("✅ Proxy Zoom initialisé avec succès");

          // Étape 3: Charger les ressources via notre proxy
          // Ajouter le CSS du SDK via notre proxy
          const zoomCss = document.createElement("link");
          zoomCss.rel = "stylesheet";
          zoomCss.href = ZOOM_SDK_URLS.proxy.css;
          zoomCss.crossOrigin = "anonymous";
          document.head.appendChild(zoomCss);

          // Charger React via notre proxy
          const reactScript = document.createElement("script");
          reactScript.src = ZOOM_SDK_URLS.proxy.react;
          reactScript.crossOrigin = "anonymous";
          document.head.appendChild(reactScript);

          // Attendre que React soit chargé
          await new Promise((resolve, reject) => {
            reactScript.onload = resolve;
            reactScript.onerror = reject;
            setTimeout(
              () => reject(new Error("Timeout lors du chargement de React")),
              5000
            );
          });

          console.log("✅ React chargé avec succès via proxy");

          // Charger le SDK Zoom via notre proxy
          const zoomScript = document.createElement("script");
          zoomScript.src = ZOOM_SDK_URLS.proxy.sdk;
          zoomScript.crossOrigin = "anonymous";
          document.head.appendChild(zoomScript);

          // Attendre que le SDK Zoom soit chargé
          await new Promise<void>((resolve, reject) => {
            zoomScript.onload = () => {
              console.log("✅ SDK Zoom chargé avec succès via proxy");
              resolve();
            };
            zoomScript.onerror = (e) => {
              console.error("❌ Erreur lors du chargement du SDK Zoom:", e);
              reject(new Error("Échec du chargement du SDK Zoom via proxy"));
            };
            setTimeout(
              () => reject(new Error("Timeout lors du chargement du SDK Zoom")),
              8000
            );
          });

          // Vérifier si Zoom est disponible
          if (window.ZoomEmbedded) {
            console.log("✅ ZoomEmbedded disponible via proxy!");
            // Créer mais ne pas stocker le client (nous utiliserons l'iframe)
            window.ZoomEmbedded.createClient();
            console.log("✅ Client Zoom créé avec succès");
            setLoadingState("ready");
            return;
          } else {
            throw new Error(
              "ZoomEmbedded n'est pas disponible après chargement"
            );
          }
        } catch (proxyError) {
          console.error("❌ Échec du chargement via proxy:", proxyError);
          // Si la tentative avec notre proxy échoue, passons à l'iframe
          console.log("🔄 Passage au mode iframe via proxy");
          setSdkFailed(true);
          setLoadingState("error");
          setErrorMessage(
            "Le SDK Zoom ne peut pas être chargé. Utilisation du mode iframe via notre proxy."
          );
        }
      } catch (error) {
        console.error("❌ Erreur lors du chargement des scripts Zoom:", error);
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Erreur lors du chargement du SDK Zoom"
        );
        setLoadingState("error");
        setSdkFailed(true);
      }
    };

    loadZoomScripts();
  }, [userName, userEmail]);

  // Gérer les erreurs globales du SDK
  useEffect(() => {
    // Intercepter les erreurs globales liées au chargement des scripts
    const handleError = (event: ErrorEvent) => {
      // Si l'erreur concerne les scripts Zoom
      if (event.filename && event.filename.includes("zoom")) {
        console.error("Erreur de chargement du SDK Zoom:", event);
        setErrorMessage(
          "Erreur lors du chargement des ressources Zoom. Veuillez vérifier votre connexion."
        );
        setLoadingState("error");
      }
    };

    window.addEventListener("error", handleError);

    return () => {
      window.removeEventListener("error", handleError);
    };
  }, []);

  // Initialiser la réunion une fois le client disponible
  useEffect(() => {
    const startMeeting = async () => {
      try {
        // Since we're using iframe solution, we immediately set error state
        setLoadingState("error");
        setErrorMessage(
          "Les ressources Zoom sont bloquées par CORS ou type MIME incorrect. Veuillez essayer le mode iframe ou l'application Zoom."
        );
        console.error(
          "❌ Chargement direct du SDK Zoom impossible en raison des restrictions CORS/MIME. Utilisez le mode iframe ou l'application Zoom."
        );
        return;
      } catch (error) {
        console.error("❌ Erreur lors du démarrage de la réunion Zoom:", error);
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Erreur lors du démarrage de la réunion Zoom"
        );
        setLoadingState("error");
      }
    };

    if (client && zoomContainer.current) {
      console.log("🚀 Client et conteneur prêts, démarrage de la réunion...");
      startMeeting();
    }
  }, [client, meetingNumber, passWord, userName, userEmail]);

  // Afficher des informations de diagnostic dans le composant ZoomMeeting
  if (loadingState === "ready") {
    // Ajouter un diagnostic visuel pour identifier si l'élément Zoom est correctement injecté
    setTimeout(() => {
      const zmmtgRoot = document.getElementById("zmmtg-root");
      console.log("📊 État du conteneur Zoom (après 1s):", {
        rootExists: !!zmmtgRoot,
        childrenCount: zmmtgRoot?.childNodes.length || 0,
        hasMeetingClient: !!zmmtgRoot?.querySelector(".meeting-client"),
        hasJoinAudioBtn: !!zmmtgRoot?.querySelector(".join-audio-container"),
        allClasses: Array.from(zmmtgRoot?.getElementsByTagName("*") || [])
          .map((el) => el.className)
          .filter(Boolean)
          .slice(0, 10),
      });
    }, 1000);

    // Vérification plus approfondie après 3 secondes
    setTimeout(() => {
      const zmmtgRoot = document.getElementById("zmmtg-root");
      console.log("📊 État du conteneur Zoom (après 3s):", {
        rootExists: !!zmmtgRoot,
        childrenCount: zmmtgRoot?.childNodes.length || 0,
        hasMeetingClient: !!zmmtgRoot?.querySelector(".meeting-client"),
        hasJoinAudioBtn: !!zmmtgRoot?.querySelector(".join-audio-container"),
        containsDivs: !!zmmtgRoot?.querySelectorAll("div").length,
        elementTree: zmmtgRoot
          ? "Premier niveau: " +
            Array.from(zmmtgRoot.children)
              .map((c) => c.tagName + (c.className ? "." + c.className : ""))
              .join(", ")
          : "Pas de zmmtg-root",
      });
    }, 3000);
  }

  if (loadingState === "loading") {
    return (
      <div ref={zoomContainer} id="zmmtg-root" className="w-full h-full">
        <div
          id="zoom-loading"
          className="flex items-center justify-center h-full"
        >
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-white">Chargement de la réunion...</p>
            <p className="text-white text-sm mt-2">Sujet: {topic}</p>

            {/* Bouton de secours qui apparaît après 5 secondes */}
            <div className="mt-6">
              <button
                onClick={() =>
                  window.open(
                    passWord
                      ? `https://zoom.us/j/${meetingNumber}?pwd=${passWord}`
                      : `https://zoom.us/j/${meetingNumber}`,
                    "_blank"
                  )
                }
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded"
              >
                Problème de chargement? Ouvrir dans Zoom
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadingState === "error" || sdkFailed) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded max-w-md">
          <p className="font-bold">Erreur de connexion</p>
          <p>{errorMessage}</p>
          <p className="mt-4 text-sm">
            Si l'erreur persiste, veuillez contacter l'assistance technique ou
            essayer de rejoindre via l'application Zoom directement.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div ref={zoomContainer} id="zmmtg-root" className="w-full h-full">
      {/* Zoom SDK injectera l'interface de la réunion ici */}
    </div>
  );
};

// Composant pour afficher le contenu de la réunion intégrée
const MeetingView: React.FC<{
  meeting: Meeting;
  onClose: () => void;
}> = ({ meeting, onClose }) => {
  console.log("🔍 MeetingView - Rendu initial", {
    meetingId: meeting.meetingId,
    topic: meeting.topic,
  });

  // Récupérer les informations de l'utilisateur connecté
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [sdkFailed, setSdkFailed] = useState(false);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [useIframeMode, setUseIframeMode] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const meetingContainerRef = useRef<HTMLDivElement>(null);

  // Définir le statut d'échec immédiatement pour passer à la méthode iframe
  useEffect(() => {
    setSdkFailed(true);
    setErrorDetails(
      "Le chargement du SDK Zoom a été bloqué par CORS/MIME. Utilisez le mode iframe ou l'application Zoom externe."
    );
  }, []);

  useEffect(() => {
    console.log("🔍 MeetingView - Chargement des infos utilisateur");
    const userStr = localStorage.getItem("user");
    console.log(
      "🔍 MeetingView - User string from localStorage:",
      userStr ? "présent" : "absent"
    );

    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        // Construire le nom complet à partir des informations retournées par l'API
        const firstName = user.firstName || "";
        const lastName = user.lastName || "";
        const fullName = `${firstName} ${lastName}`.trim();

        console.log("🔍 MeetingView - Infos utilisateur parsées:", {
          firstName: firstName || "absent",
          lastName: lastName || "absent",
          fullName: fullName || "absent",
          email: user.email || "absent",
        });

        // Définir le nom d'utilisateur pour Zoom
        if (fullName) {
          setUserName(fullName);
        } else {
          // Fallback si nom et prénom ne sont pas disponibles
          setUserName("Utilisateur GuidePro");
          console.warn(
            "❗ Nom d'utilisateur non trouvé, utilisation du nom par défaut"
          );
        }

        setUserEmail(user.email || "");
      } catch (e) {
        console.error(
          "❌ MeetingView - Erreur lors de la récupération des infos utilisateur:",
          e
        );
        // Valeurs par défaut en cas d'erreur
        setUserName("Utilisateur GuidePro");
        setUserEmail("");
      }
    } else {
      console.error(
        "❌ MeetingView - Pas d'utilisateur trouvé dans localStorage"
      );
      // Valeurs par défaut si pas d'utilisateur
      setUserName("Utilisateur GuidePro");
      setUserEmail("");
    }

    // Détecter si le SDK a des problèmes de chargement
    const timeoutId = setTimeout(() => {
      // Si après 5 secondes, l'élément zmmtg-root n'est pas correctement initialisé
      const zmmtgRoot = document.getElementById("zmmtg-root");
      if (zmmtgRoot && !zmmtgRoot.querySelector(".meeting-client")) {
        console.warn("SDK Zoom non chargé correctement après 5 secondes");
        setSdkFailed(true);
      }
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  // Fonction pour obtenir l'URL correcte pour l'iframe via le proxy
  const getIframeUrl = () => {
    // Logger les informations pour le debugging
    console.log("🔍 Création de l'URL d'iframe avec les données:", {
      meetingId: meeting.zoomMeetingId || meeting.meetingId,
      meetingPassword: meeting.password || "",
    });

    // Utiliser systématiquement le zoomMeetingId s'il est disponible, sinon utiliser meetingId
    const meetingId = meeting.zoomMeetingId || meeting.meetingId;
    const password = meeting.password || "";

    // Utiliser l'URL Zoom Meeting client le plus récent avec des paramètres optimisés
    // Paramètres ajoutés: audio=true, video=true, web=true pour forcer ces fonctionnalités
    return `https://zoom.us/wc/join/${meetingId}?pwd=${encodeURIComponent(
      password
    )}&webinar=false&audio=true&video=true&web=true&meeting_result=false&un=${encodeURIComponent(
      userName || "Utilisateur GuidePro"
    )}`;
  };

  // Fonction pour basculer en mode plein écran
  const toggleFullscreen = () => {
    if (!isFullscreen && meetingContainerRef.current) {
      if (meetingContainerRef.current.requestFullscreen) {
        meetingContainerRef.current.requestFullscreen().catch((err) => {
          console.error(
            `Erreur de passage en plein écran: ${err.message} (${err.name})`
          );
        });
      }
    } else if (document.exitFullscreen) {
      document.exitFullscreen().catch((err) => {
        console.error(
          `Erreur en quittant le plein écran: ${err.message} (${err.name})`
        );
      });
    }
  };

  // Écouter les changements d'état plein écran
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // La fonction resizeIframe a été supprimée car elle n'est plus utilisée

  // Fonction améliorée pour ouvrir en popup qui résout les problèmes CORS
  const openZoomPopup = () => {
    // Utiliser une URL directe vers le client Web pour éviter les problèmes CORS
    const url = `https://zoom.us/wc/${
      meeting.zoomMeetingId || meeting.meetingId
    }`;
    const params = new URLSearchParams({
      pwd: meeting.password || "",
      success_url: window.location.href, // URL de retour en cas de succès
      failure_url: window.location.href, // URL de retour en cas d'échec
    });

    const finalUrl = `${url}?${params.toString()}`;
    const width = Math.min(1200, window.innerWidth * 0.9);
    const height = Math.min(800, window.innerHeight * 0.9);
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;

    const popup = window.open(
      finalUrl,
      "ZoomMeeting",
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes,location=yes`
    );

    if (popup) {
      // Fermer cette vue après l'ouverture de la popup
      setTimeout(() => {
        onClose();
      }, 500);
    } else {
      alert(
        "Votre navigateur a bloqué la fenêtre popup. Veuillez autoriser les popups pour ce site."
      );
    }
  };

  // Si SDK a échoué, proposer différentes solutions alternatives
  if (sdkFailed || (!userName && !userEmail)) {
    return (
      <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex flex-col items-center justify-center p-4">
        <div
          ref={meetingContainerRef}
          className="bg-gray-900 w-full max-w-6xl h-[80vh] rounded-lg overflow-hidden flex flex-col relative"
        >
          <div className="bg-gray-800 p-4 flex justify-between items-center">
            <h2 className="text-white font-bold text-xl">{meeting.topic}</h2>
            <div className="flex items-center space-x-2">
              {useIframeMode && (
                <div className="flex space-x-1">
                  <button
                    onClick={toggleFullscreen}
                    className="text-white hover:text-blue-300"
                    title="Plein écran"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                    </svg>
                  </button>
                </div>
              )}
              <button
                onClick={onClose}
                className="text-white hover:text-red-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </div>

          {useIframeMode ? (
            <div className="flex-grow relative">
              {/* Overlay de protection contre les erreurs CORS */}
              <div className="absolute inset-0 bg-transparent z-10 pointer-events-none"></div>

              <iframe
                ref={iframeRef}
                src={getIframeUrl()}
                className="w-full h-full border-0"
                allow="camera *; microphone *; fullscreen; display-capture; autoplay; clipboard-read; clipboard-write; web-share; encrypted-media; geolocation"
                onError={(e) => {
                  console.error("Iframe error:", e);
                  setErrorDetails(
                    "Problème de chargement de la réunion dans l'iframe"
                  );
                  setUseIframeMode(false);
                }}
              ></iframe>

              {/* Overlay avec instructions et bouton pour rejoindre via l'application Zoom */}
              <div
                className="absolute top-0 left-0 right-0 bg-black bg-opacity-70 text-white p-3 text-sm flex justify-between items-center"
                style={{ zIndex: 100 }}
              >
                <div className="flex items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2 text-yellow-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>
                    Les contrôles audio/vidéo peuvent être limités dans le
                    navigateur.
                  </span>
                </div>
                <button
                  onClick={() =>
                    window.open(
                      `zoommtg://zoom.us/join?action=join&confno=${
                        meeting.zoomMeetingId || meeting.meetingId
                      }&pwd=${
                        meeting.password || ""
                      }&zc=0&browser=chrome&uname=${encodeURIComponent(
                        userName || "Utilisateur GuidePro"
                      )}`,
                      "_blank"
                    )
                  }
                  className="ml-2 bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1 rounded"
                >
                  Rejoindre avec l'app Zoom
                </button>
              </div>

              {/* Message d'erreur qui apparaît après 5 secondes si l'iframe est blanc */}
              <div
                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-70 opacity-0 transition-opacity duration-500"
                style={{ opacity: "0" }}
                id="iframe-error-message"
              >
                <div className="bg-white p-4 rounded-lg max-w-md text-center">
                  <p className="text-lg font-bold mb-2">
                    Problème de chargement
                  </p>
                  <p className="mb-4">
                    L'interface Zoom ne peut pas être chargée dans
                    l'application.
                  </p>
                  <button
                    onClick={openZoomPopup}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-md"
                  >
                    Ouvrir dans une fenêtre externe
                  </button>
                </div>
              </div>

              {/* Script qui vérifie si l'iframe reste blanc */}
              <script
                dangerouslySetInnerHTML={{
                  __html: `
                setTimeout(() => {
                  const iframe = document.querySelector('iframe');
                  const errorMsg = document.getElementById('iframe-error-message');
                  if (iframe && (!iframe.contentWindow || iframe.contentWindow.document.body.children.length === 0)) {
                    if (errorMsg) errorMsg.style.opacity = '1';
                  }
                }, 5000);
              `,
                }}
              />
            </div>
          ) : (
            <div className="flex-grow flex items-center justify-center">
              <div className="bg-gray-800 p-8 rounded-lg max-w-lg text-center">
                <svg
                  className="w-16 h-16 text-yellow-500 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  ></path>
                </svg>
                <h3 className="text-xl font-bold text-white mb-2">
                  Problème de chargement Zoom
                </h3>
                <p className="mb-4 text-gray-300">
                  {errorDetails ||
                    (!userName && !userEmail
                      ? "Impossible de récupérer vos informations utilisateur. Veuillez vous reconnecter."
                      : "Le SDK Zoom ne peut pas être chargé en raison de restrictions du navigateur (CORS/MIME/Cookies).")}
                </p>
                <div className="bg-gray-700 p-3 rounded-md text-left mb-4">
                  <p className="text-sm text-gray-300">
                    <span className="font-bold">ID:</span>{" "}
                    {meeting.zoomMeetingId || meeting.meetingId}
                  </p>
                  {meeting.password && (
                    <p className="text-sm text-gray-300">
                      <span className="font-bold">Mot de passe:</span>{" "}
                      {meeting.password}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <button
                    onClick={() => setUseIframeMode(true)}
                    className="bg-green-600 hover:bg-green-700 text-white font-medium px-6 py-3 rounded-md w-full flex items-center justify-center"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      ></path>
                    </svg>
                    Essayer avec iframe
                  </button>

                  <button
                    onClick={openZoomPopup}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-md w-full flex items-center justify-center"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      ></path>
                    </svg>
                    Ouvrir dans une fenêtre
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="p-4 bg-gray-800 border-t border-gray-700">
            <p className="text-gray-300 text-sm flex items-center justify-between">
              <span className="flex items-center">
                <Calendar size={14} className="mr-2" />
                <span className="mr-4">{formatDate(meeting.startTime)}</span>
                <Clock size={14} className="mx-2" />
                <span>{formatTime(meeting.startTime)}</span>
                <span className="ml-4">
                  Durée: {formatDuration(meeting.duration)}
                </span>
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex flex-col items-center justify-center p-4">
      <div className="bg-gray-900 w-full max-w-6xl h-[80vh] rounded-lg overflow-hidden flex flex-col relative">
        <div className="bg-gray-800 p-4 flex justify-between items-center">
          <h2 className="text-white font-bold text-xl">{meeting.topic}</h2>
          <button onClick={onClose} className="text-white hover:text-red-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="flex-grow relative">
          {userName && userEmail ? (
            <ZoomMeeting
              meetingNumber={meeting.meetingId}
              passWord={meeting.password || ""}
              userName={userName}
              userEmail={userEmail}
              topic={meeting.topic}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-white">
                Chargement des informations utilisateur...
              </p>
            </div>
          )}
        </div>

        <div className="p-4 bg-gray-800 border-t border-gray-700">
          <p className="text-gray-300 text-sm flex items-center justify-between">
            <span className="flex items-center">
              <Calendar size={14} className="mr-2" />
              <span className="mr-4">{formatDate(meeting.startTime)}</span>
              <Clock size={14} className="mx-2" />
              <span>{formatTime(meeting.startTime)}</span>
              <span className="ml-4">
                Durée: {formatDuration(meeting.duration)}
              </span>
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

const LiveDirect: React.FC = () => {
  console.log(" LiveDirect - Rendu du composant principal");
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"upcoming" | "live" | "past">(
    "live"
  );
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [showMeetingView, setShowMeetingView] = useState(false);

  // State pour les réunions
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [meetingsLoading, setMeetingsLoading] = useState<boolean>(false);
  const [meetingsError, setMeetingsError] = useState<string | null>(null);

  // Fonction pour ouvrir une réunion dans la plateforme
  const openMeetingInPlatform = (meeting: Meeting) => {
    console.log("📌 LiveDirect - Ouverture de réunion", {
      meetingId: meeting.meetingId,
      topic: meeting.topic,
      startTime: meeting.startTime,
    });
    navigate(`/etudiant/live/${meeting.id}`);
  };

  // Fonction pour fermer la vue de la réunion
  const closeMeetingView = () => {
    console.log("📌 LiveDirect - Fermeture de la vue de réunion");
    setShowMeetingView(false);
    setSelectedMeeting(null);
  };

  // Fetch user meetings on component mount
  useEffect(() => {
    console.log("📌 LiveDirect - Démarrage du chargement des réunions");
    const fetchMeetings = async () => {
      setMeetingsLoading(true);
      try {
        const accessToken = localStorage.getItem("accessToken");
        const response = await fetch(
          API_ENDPOINTS.MEETINGS.GET_MEETINGS_BY_USER,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error(
            `Erreur lors de la récupération des réunions: ${response.status}`
          );
        }

        const data = await response.json();
        setMeetings(data);
      } catch (err) {
        setMeetingsError(
          err instanceof Error ? err.message : "Une erreur est survenue"
        );
        console.error("Erreur de chargement des réunions:", err);
      } finally {
        setMeetingsLoading(false);
      }
    };

    fetchMeetings();
  }, []);

  // Filtrer les réunions en fonction de la recherche et de l'onglet actif
  const filteredMeetings = meetings
    .filter(
      (meeting) =>
        meeting.topic.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (meeting.description &&
          meeting.description.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    .filter(
      (meeting) =>
        getMeetingStatus(meeting.startTime, meeting.duration) === activeTab
    );

  // Vérifier s'il y a des résultats à afficher
  const hasResults = filteredMeetings.length > 0;

  return (
    <div className="flex flex-col min-h-screen bg-[#fefaf6]">
      {/* Afficher la vue de réunion si une réunion est sélectionnée */}
      {showMeetingView && selectedMeeting && (
        <MeetingView meeting={selectedMeeting} onClose={closeMeetingView} />
      )}

      {/* Titre principal */}
      <div className="bg-[#fefaf6] py-2 relative">
        <div className="container mx-auto px-4">
          <h1 className="text-2xl font-bold text-left text-gray-800">
            Live/Direct
          </h1>
        </div>
      </div>

      {/* Contenu principal avec recherche et filtres */}
      <div className="container mx-auto px-4 pt-2 pb-8">
        {/* Barre de recherche */}
        <div className="relative mb-8 mobile:mb-5 tablet:mb-6">
          <input
            type="text"
            placeholder="Rechercher une réunion..."
            className="w-full max-w-md border border-gray-300 rounded-full px-4 py-2 pl-10 mobile:py-1.5 tablet:py-1.5 focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-green-600 transition-colors"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 mobile:top-2 tablet:top-2" />
        </div>

        {/* Onglets de filtrage */}
        <div className="flex border-b border-gray-200 mb-8 mobile:mb-6 tablet:mb-6 mobile:overflow-x-auto tablet:overflow-x-auto">
          <button
            className={`px-4 py-2 font-medium whitespace-nowrap mobile:px-2 mobile:py-1 mobile:text-xs tablet:px-3 tablet:py-1.5 tablet:text-sm ${
              activeTab === "live"
                ? "border border-orange-500 text-orange-500 font-medium"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("live")}
          >
            EN DIRECT
          </button>
          <button
            className={`px-4 py-2 font-medium whitespace-nowrap mobile:px-2 mobile:py-1 mobile:text-xs tablet:px-3 tablet:py-1.5 tablet:text-sm ${
              activeTab === "upcoming"
                ? "border border-orange-500 text-orange-500 font-medium"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("upcoming")}
          >
            À VENIR
          </button>
          <button
            className={`px-4 py-2 font-medium whitespace-nowrap mobile:px-2 mobile:py-1 mobile:text-xs tablet:px-3 tablet:py-1.5 tablet:text-sm ${
              activeTab === "past"
                ? "border border-orange-500 text-orange-500 font-medium"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("past")}
          >
            ARCHIVES
          </button>
        </div>

        {/* Liste des réunions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10 grid-flow-row auto-rows-fr">
          {meetingsLoading ? (
            // Afficher un chargeur pendant le chargement des réunions
            <div className="col-span-full flex justify-center items-center py-10">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
            </div>
          ) : meetingsError ? (
            // Afficher le message d'erreur si la récupération a échoué
            <div className="col-span-full flex justify-center items-center py-10">
              <div className="text-red-500">Erreur: {meetingsError}</div>
            </div>
          ) : (
            <>
              {activeTab === "live" && (
                <>
                  {/* Afficher les réunions en direct */}
                  {filteredMeetings.map((meeting) => (
                    <LiveMeetingCard
                      key={`meeting-${meeting.id}`}
                      meeting={meeting}
                      onJoinMeeting={openMeetingInPlatform}
                    />
                  ))}

                  {/* Message si aucun résultat */}
                  {!hasResults && (
                    <div className="col-span-full text-center py-10">
                      <p className="text-gray-500">
                        Aucune réunion en direct trouvée.
                      </p>
                    </div>
                  )}
                </>
              )}

              {activeTab === "upcoming" && (
                <>
                  {/* Afficher les réunions à venir */}
                  {filteredMeetings.map((meeting) => (
                    <UpcomingMeetingCard
                      key={`meeting-${meeting.id}`}
                      meeting={meeting}
                      onJoinMeeting={openMeetingInPlatform}
                    />
                  ))}

                  {/* Message si aucun résultat */}
                  {!hasResults && (
                    <div className="col-span-full text-center py-10">
                      <p className="text-gray-500">
                        Aucune réunion à venir trouvée.
                      </p>
                    </div>
                  )}
                </>
              )}

              {activeTab === "past" && (
                <>
                  {/* Afficher les réunions passées */}
                  {filteredMeetings.map((meeting) => (
                    <PastMeetingCard
                      key={`meeting-${meeting.id}`}
                      meeting={meeting}
                      onJoinMeeting={openMeetingInPlatform}
                    />
                  ))}

                  {/* Message si aucun résultat */}
                  {!hasResults && (
                    <div className="col-span-full text-center py-10">
                      <p className="text-gray-500">
                        Aucune réunion archivée trouvée.
                      </p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveDirect;
